/* Where someone came from, captured once and kept.
 *
 * PostHog knows this already, but it means logging in to PostHog and digging,
 * which is exactly what the admin dashboard exists to avoid. This puts the
 * answer next to the person in the users table.
 *
 * First touch, not last. Someone who arrives from an Instagram Reel, wanders
 * off, and comes back a week later by typing the address is an Instagram
 * sign-up: the Reel did the work. Last touch would call that one "Direct" and
 * quietly credit nothing.
 */

const KEY = 'mimique.attribution';

// Referring domains worth naming. Everything else falls through to the bare
// domain, which is still more use than "Other" — an unexpected referrer is a
// thing worth seeing, not hiding.
const SOURCES = [
    [/(^|\.)instagram\.com$/, 'Instagram'],
    [/(^|\.)tiktok\.com$/, 'TikTok'],
    [/(^|\.)facebook\.com$/, 'Facebook'],
    [/(^|\.)linkedin\.com$/, 'LinkedIn'],
    [/^lnkd\.in$/, 'LinkedIn'],
    [/^t\.co$/, 'X'],
    [/(^|\.)google\./, 'Google'],
    [/(^|\.)bing\.com$/, 'Bing'],
    [/(^|\.)duckduckgo\.com$/, 'DuckDuckGo'],
    [/(^|\.)reddit\.com$/, 'Reddit'],
    [/(^|\.)youtube\.com$/, 'YouTube'],
    [/(^|\.)leanneoleary\.com$/, 'Own blog'],
    [/(^|\.)pwa\.directory$/, 'pwa.directory'],
];

const labelForDomain = (domain) => {
    for (const [pattern, label] of SOURCES) {
        if (pattern.test(domain)) return label;
    }
    return domain;
};

const read = () => {
    try {
        const raw = localStorage.getItem(KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        return null;
    }
};

/** Work out where this visit came from. Null when there is nothing to say. */
const observe = () => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = (params.get('utm_source') || '').trim().slice(0, 60);
    const utmMedium = (params.get('utm_medium') || '').trim().slice(0, 60);
    const utmCampaign = (params.get('utm_campaign') || '').trim().slice(0, 80);

    let referrerDomain = '';
    try {
        const ref = document.referrer;
        if (ref) {
            const host = new URL(ref).hostname.replace(/^www\./, '');
            // Our own pages are not a source. Without this, anyone who lands on
            // the blog and clicks through to sign up is recorded as arriving
            // from mimique.app, which says nothing.
            if (host !== window.location.hostname.replace(/^www\./, '')) {
                referrerDomain = host;
            }
        }
    } catch (err) {
        /* A referrer we can't parse is the same as no referrer. */
    }

    // A tagged link is the strongest signal there is: it was built on purpose,
    // so it beats whatever the browser reports as the referrer.
    const label = utmSource
        ? utmSource
        : referrerDomain
            ? labelForDomain(referrerDomain)
            : 'Direct';

    return {
        label,
        referrer: referrerDomain,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        landing_path: window.location.pathname.slice(0, 120),
        first_seen: new Date().toISOString(),
    };
};

/** Record the first visit, if this is one. Safe to call on every page load. */
export const captureAttribution = () => {
    if (read()) return;   // First touch wins. Never overwritten.
    try {
        localStorage.setItem(KEY, JSON.stringify(observe()));
    } catch (err) {
        /* Private mode, or storage full. Sign-up just goes through unattributed. */
    }
};

/** What to send with a sign-up. Null when we never managed to record anything. */
export const getAttribution = () => read();
